import axios, { isAxiosError } from 'axios'
import type { User } from 'firebase/auth'

import type { BasketItem } from '@/context/basketContext'

import { copyRequestCorrelation } from '@/lib/api/rest/requestCorrelation'
import { appendLeadId } from '@/lib/attribution/leadId'

import {
  type CancellationDiscount,
  getPaymentServiceClient,
  type CancelSubscriptionRequest,
  type CancellationInterventionsResponse,
  type CancelledSubscriptionDetails,
  type CheckoutLineItem,
  type CheckoutSessionCreationErrorResponse,
  CheckoutSessionCreationErrorResponseType,
  type CreateCheckoutSessionRequest,
  type CreateCheckoutSessionResponse,
  type ExpressCheckoutRequest,
  type FetchPublicPricesProductType,
  type GetSubscriptionsPriceProductType,
  type ListSubscriptionStatus,
  type ModifySubscriptionItemsRequest,
  type MoveBillingRequest,
  type Price,
  type ProductAvailability,
  type PublicCustomerResponse,
  type Subscription,
  type SubscriptionItemInput,
  type SwitchOnRenewalResponse,
  type SwitchOptionsResponse,
} from '@/clients/payment-service'

import { baseAxiosConfig } from './api/rest/config'
import type { CheckoutCountry } from './checkout/types'
import { type AuthSource, getAuthHeaders, getUserAuthHeaders } from './getUserAuthHeaders'
import { getDeterministicUuid } from './utils/cryptoUtils'

export type PriceLookupKey = string

export class CreateCheckoutSessionError extends Error {
  readonly status: number
  readonly type: CheckoutSessionCreationErrorResponseType | undefined

  constructor(status: 422, message: string, type: CheckoutSessionCreationErrorResponseType)
  constructor(status: number, message: string)
  constructor(status: number, message: string, type?: CheckoutSessionCreationErrorResponseType) {
    super(message)
    this.name = 'CreateCheckoutSessionError'
    this.status = status
    this.type = type
  }
}

const client = getPaymentServiceClient(
  axios.create({ ...baseAxiosConfig, baseURL: process.env.NEXT_PUBLIC_PAYMENT_SERVICE_BASE }),
)

/** `API-Version` header for POST /public/session: `1` = legacy (email + eligibility + CRM), `2` = session-only. */
export const CHECKOUT_API_VERSION = {
  LEGACY: '1',
  SESSION_ONLY: '2',
} as const
export type CheckoutApiVersion = (typeof CHECKOUT_API_VERSION)[keyof typeof CHECKOUT_API_VERSION]

type CreateSessionParams = {
  user: User
  basketItems: BasketItem[]
  countryCode: CheckoutCountry
  returnUrl: string
  promotionCodeId?: string | null
  email?: string
  incentiveDecisionTimerEnd?: Date
  apiVersion?: CheckoutApiVersion
}

export const createSession = async ({
  user,
  basketItems,
  countryCode,
  returnUrl,
  promotionCodeId,
  email,
  incentiveDecisionTimerEnd,
  apiVersion = CHECKOUT_API_VERSION.SESSION_ONLY,
}: CreateSessionParams) => {
  const sessionRequest: CreateCheckoutSessionRequest = {
    basket: basketItems.map((item) => ({
      product_id: item.priceLookupKey,
      quantity: item.quantity ?? 1,
    })),
    country_code: countryCode,
    // Stripe redirects back to return_url via a full page load (wipes the
    // in-memory lead_id), so carry attribution on the URL (WEB-903).
    return_url: appendLeadId(returnUrl),
    email: email || undefined,
    promotion_code_id: promotionCodeId || undefined,
    incentive_decision_timer_end: incentiveDecisionTimerEnd?.toISOString(),
  }
  try {
    // Defaults to v2 (session-only): email optional, no server-side eligibility/CRM; typed via CheckoutApiVersion, overridable by apiVersion.
    const session = await client.createCheckoutSession(sessionRequest, {
      headers: { ...(await getUserAuthHeaders(user)), 'API-Version': apiVersion },
    })
    return session.data
  } catch (caught) {
    const { response, message = 'Unknown error' } = isAxiosError(caught) ? caught : {}
    const status = response?.status || 500

    if (status === 422) {
      const payload = response?.data as CheckoutSessionCreationErrorResponse | undefined
      if (payload?.type) {
        // Normalize validation/business rule errors for callers
        throw copyRequestCorrelation(new CreateCheckoutSessionError(422, payload.message, payload.type), caught)
      }
    }

    throw copyRequestCorrelation(new CreateCheckoutSessionError(status, response?.data?.message || message), caught)
  }
}

/** Result of the standalone eligibility check: eligible (200) / existing_member (409) / ineligible (422). */
export type EligibilityResult =
  | { status: 'eligible' }
  | { status: 'existing_member' }
  | { status: 'ineligible'; type: CheckoutSessionCreationErrorResponseType }
export type EligibilityStatus = EligibilityResult['status']

/** Runs the standalone eligibility check ahead of session creation, mapping status codes to an explicit result. */
export const checkEligibility = async (
  user: User,
  basketItems: BasketItem[],
  countryCode: CheckoutCountry,
  email?: string,
): Promise<EligibilityResult> => {
  try {
    await client.checkEligibility(
      {
        basket: basketItems.map((item) => ({ product_id: item.priceLookupKey, quantity: item.quantity ?? 1 })),
        country_code: countryCode,
        email: email || undefined,
      },
      { headers: await getUserAuthHeaders(user) },
    )
    return { status: 'eligible' }
  } catch (caught) {
    if (isAxiosError(caught)) {
      switch (caught.response?.status) {
        case 409:
          return { status: 'existing_member' }
        case 422: {
          const payload = caught.response?.data as CheckoutSessionCreationErrorResponse | undefined
          return {
            status: 'ineligible',
            type: payload?.type ?? CheckoutSessionCreationErrorResponseType.CHECKOUT_VALIDATION_ERROR,
          }
        }
      }
    }
    throw caught
  }
}

export const updateCheckoutSession = async (
  user: User,
  sessionId: string,
  countryCode: CheckoutCountry,
  basket: Array<{ id?: string; productId: string; quantity: number }>,
) => {
  const response = await client.updateCheckoutSession(
    {
      session_id: sessionId,
      country_code: countryCode,
      basket: basket.map((item) => ({
        id: item.id,
        product_id: item.productId,
        quantity: item.quantity,
      })),
    },
    { headers: await getUserAuthHeaders(user) },
  )
  return response.data
}

/**
 * Returns a function that performs the checkout session update via native fetch.
 * Designed to be passed to Stripe's `checkout.runServerUpdate()` which
 * expects a `() => Promise<Response>` and auto-refreshes the SDK state
 * after the server update completes.
 */
export const buildUpdateCheckoutSessionFetch = async (
  user: User,
  sessionId: string,
  countryCode: CheckoutCountry,
  basket: Array<{ id?: string; priceLookupKey: string; quantity: number }>,
): Promise<() => Promise<Response>> => {
  const authHeaders = await getUserAuthHeaders(user)

  return () =>
    fetch(`${process.env.NEXT_PUBLIC_PAYMENT_SERVICE_BASE}/public/session`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        session_id: sessionId,
        country_code: countryCode,
        basket: basket.map((item) => ({
          id: item.id || undefined,
          product_id: item.priceLookupKey,
          quantity: item.quantity,
        })),
      }),
    })
}

/** Takes an {@link AuthSource}: the in-app cross-sell confirmation reads its
 *  session with the app's token, having no Firebase user to sign the call. */
export const getSessionStatus = async (
  auth: AuthSource,
  countryCode: CheckoutCountry,
  sessionId: string,
  guestEmail?: string,
) => {
  const session = await client.getCheckoutSessionDetails(
    { country_code: countryCode, session_id: sessionId, email: guestEmail },
    { headers: await getAuthHeaders(auth) },
  )
  return session.data
}

/**
 * Fetches the current user's customer data from the payment service (GET /public/customers/me).
 */
export const getCustomer = async (user: User): Promise<PublicCustomerResponse> => {
  const response = await client.getCustomer({ headers: await getUserAuthHeaders(user) })
  return response.data
}

export const getBillingPortalUrl = async (user: User) => {
  const billingPortal = await client.getBillingPortalUrl({ headers: await getUserAuthHeaders(user) })
  return billingPortal.data.url
}

export const getPaymentSubscriptions = async (
  user: User,
  subscriptionStatus?: ListSubscriptionStatus,
  priceProductType?: GetSubscriptionsPriceProductType,
  includeSchedule?: boolean,
  includeDiscounts?: boolean,
  includeUpcomingInvoicePreview?: boolean,
) => {
  const response = await client.getSubscriptions(
    { subscriptionStatus, priceProductType, includeSchedule, includeDiscounts, includeUpcomingInvoicePreview },
    { headers: await getUserAuthHeaders(user) },
  )
  return response.data
}

export const getProductsAvailability = async (
  user: User,
  countryCode?: string,
): Promise<{ [key: string]: ProductAvailability }> => {
  const response = await client.getProductsAvailability({ countryCode }, { headers: await getUserAuthHeaders(user) })
  return response.data
}

export const movePhysicalProductSubscriptionBillingDate = async (
  user: User,
  identifier: string,
  request: MoveBillingRequest,
) => {
  const response = await client.movePhysicalProductSubscriptionBillingDate(identifier, request, {
    headers: await getUserAuthHeaders(user),
  })
  return response.data
}

/** Overrides `baseAxiosConfig`'s 30s: this call blocks the account pages' first paint. */
const SWITCH_OPTIONS_TIMEOUT_MS = 3_000

/** The plans this subscription can switch to, or why it cannot switch at all. Does not catch. */
export const getSwitchOptions = async (user: User, subscriptionId: string): Promise<SwitchOptionsResponse> => {
  const response = await client.getSwitchOptions(subscriptionId, {
    headers: await getUserAuthHeaders(user),
    timeout: SWITCH_OPTIONS_TIMEOUT_MS,
  })
  return response.data
}

type GetPublicPricesOptions = {
  productType?: FetchPublicPricesProductType
  lookupKeys?: Iterable<string>
  visible?: boolean
  live?: boolean
}

/**
 * Unauthenticated price lookup against /public/prices — for SSR/anonymous
 * browsing (e.g. marketing pages). `countryCode` selects the Stripe price
 * book; `productType` narrows to a single product.
 */
export const getPublicPrices = async (
  countryCode: string,
  { productType, lookupKeys, visible, live }: GetPublicPricesOptions = {},
): Promise<Array<Price>> => {
  const response = await client.fetchPublicPrices({
    countryCode,
    productType,
    lookupKeys: !lookupKeys ? undefined : Array.isArray(lookupKeys) ? lookupKeys : Array.from(lookupKeys),
    visible,
    live,
  })
  return Array.from(response.data.prices ?? [])
}

export const switchPlanBySubscriptionId = async (
  user: User,
  subscriptionId: string,
  targetPlan: string,
): Promise<SwitchOnRenewalResponse> => {
  const response = await client.switchPlanBySubscriptionId(
    subscriptionId,
    { targetPlan },
    { headers: await getUserAuthHeaders(user) },
  )
  return response.data
}

export const applyCancellationDiscount = async (user: User, subscriptionId: string): Promise<CancellationDiscount> => {
  const response = await client.applyDiscount(subscriptionId, { headers: await getUserAuthHeaders(user) })
  return response.data
}

export const modifySubscriptionItems = async (
  user: User,
  subscriptionId: string,
  items: SubscriptionItemInput[],
): Promise<Subscription> => {
  const timestampSeconds = Math.floor(Date.now() / 1000)
  const request: ModifySubscriptionItemsRequest = {
    items: Array.from(new Set(items)),
    idempotency_key: getDeterministicUuid(`${subscriptionId}_${timestampSeconds}`),
  }
  const response = await client.modifySubscriptionItems(subscriptionId, request, {
    headers: await getUserAuthHeaders(user),
  })
  return response.data
}

export const reinstateAutoRenewal = async (user: User, subscriptionId: string) => {
  const response = await client.reinstateAutoRenewal(subscriptionId, { headers: await getUserAuthHeaders(user) })
  return response.data
}

export const getCancellationInterventions = async (
  user: User,
  subscriptionId: string,
): Promise<CancellationInterventionsResponse> => {
  const response = await client.getInterventions(subscriptionId, { headers: await getUserAuthHeaders(user) })
  return response.data
}

export const cancelSubscription = async (
  user: User,
  subscriptionId: string,
  request: CancelSubscriptionRequest,
): Promise<CancelledSubscriptionDetails> => {
  const response = await client.cancel(subscriptionId, request, { headers: await getUserAuthHeaders(user) })
  return response.data
}

/** payment-service answers an expired or invalid bearer with a 401. */
export const isUnauthorisedError = (error: unknown): boolean => isAxiosError(error) && error.response?.status === 401

export const createOneClickCheckout = async (
  auth: AuthSource,
  request: {
    /** Absent for the in-app entry, which has no preceding purchase to reuse. */
    referenceSessionId?: string
    basket: { productId: string; quantity: number }[]
    countryCode: CheckoutCountry
    returnUrl: string
    promotionCodeId?: string
  },
): Promise<CreateCheckoutSessionResponse> => {
  const basketItems = request.basket.map(({ productId, quantity }): CheckoutLineItem => ({
    product_id: productId,
    quantity,
  }))

  const body: ExpressCheckoutRequest = {
    basket: basketItems,
    country_code: request.countryCode,
    return_url: appendLeadId(request.returnUrl),
    promotion_code_id: request.promotionCodeId,
  }
  try {
    const response = await client.createOneClickCheckout(
      body,
      { referenceSessionId: request.referenceSessionId },
      { headers: await getAuthHeaders(auth) },
    )
    return response.data
  } catch (caught) {
    // Same 422 normalisation as createSession: business-rule rejections (e.g.
    // trial ineligibility) surface as CreateCheckoutSessionError with a `type`
    // callers can branch on to fall back to a different basket.
    if (isAxiosError(caught)) {
      const status = caught.response?.status

      if (status === 422) {
        const payload = caught.response?.data as CheckoutSessionCreationErrorResponse | undefined
        if (payload?.type) {
          throw copyRequestCorrelation(new CreateCheckoutSessionError(status, payload.message, payload.type), caught)
        }
      }
    }

    throw caught
  }
}
